import React, { useState, useCallback } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import { useRoute, useNavigation, useFocusEffect, RouteProp, NavigationProp } from '@react-navigation/native';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useResponsive } from '../../hooks/useResponsive';
import { PageContainer, Navbar, Breadcrumb, ActivityCalendar } from '../../components';
import ActivityHeader from './components/ActivityHeader';
import DetailStatsRow from './components/DetailStatsRow';
import KPIsSection from './components/KPIsSection';
import SessionsSection from './components/SessionsSection';
import OutputsSection from './components/OutputsSection';
import DevelopmentPlan from './components/DevelopmentPlan';
import DetailTeachersTable from './components/DetailTeachersTable';
import StudentsTable from './components/StudentsTable';
import TalentsSkeleton from './components/TalentsSkeleton';
import { talentActivitiesApi, talentProjectsApi } from '../../services/apiService';
import { API_BASE_HOST } from '../../constants/config';
import { ActivityDetailData } from './types';
import { CalendarEvent } from '../../components/ActivityCalendar/types';
import { MainStackParamList } from '../../navigation/MainNavigator';

/** Convert activity sessions to CalendarEvent[] for the calendar */
function sessionsToCalendarEvents(
  sessions: { id: number; date: string; startTime: string; endTime: string }[],
  activityTitle: string,
  studentsCount: number,
): CalendarEvent[] {
  return sessions.map((s) => ({
    id: s.id,
    title: activityTitle,
    date: s.date,
    startTime: s.startTime.substring(0, 5),
    endTime: s.endTime.substring(0, 5),
    category: 'painting' as const, // teal color
    participantCount: studentsCount,
  }));
}

type ScreenRouteProp = RouteProp<MainStackParamList, 'TalentActivityDetail'>;
type ScreenNavProp = NavigationProp<MainStackParamList>;

export default function TalentActivityDetailScreen() {
  const route = useRoute<ScreenRouteProp>();
  const navigation = useNavigation<ScreenNavProp>();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const { user } = useAuth();
  const isCoordinator = user?.profile?.user_type === 'coordinator';
  const readOnly = user?.profile?.user_type === 'strategy_team';
  const isStudent = user?.profile?.user_type === 'student';
  const noCreate = readOnly || isCoordinator || isStudent;
  const { isMobile } = useResponsive();
  const containerPadding = isMobile ? 12 : 24;

  const activityId = route.params?.id;
  const entityName = route.params?.entityName;

  const [isLoading, setIsLoading] = useState(true);
  const [projectId, setProjectId] = useState<number | undefined>();
  const [specializationId, setSpecializationId] = useState<number | undefined>();
  const [data, setData] = useState<ActivityDetailData>({
    id: 0, title: '', dateRange: '', description: '', weight: 0, location: '',
    studentsCount: 0, teachersCount: 0, kpisCount: 0, sessionsCount: 0,
    overallProgress: 0, statusDistribution: { excellent: 0, good: 0, needsImprovement: 0 },
    kpis: [], sessions: [], outputs: [],
    sustainabilityPlan: { title: '', content: '' },
  });
  // Calendar events derived from sessions (not separate state)
  const calendarEvents = sessionsToCalendarEvents(data.sessions, data.title, data.studentsCount);
  // Center calendar on the first session date so events are visible
  const firstSessionDate = data.sessions.length > 0 && data.sessions[0].date
    ? new Date(data.sessions[0].date + 'T00:00:00')
    : undefined;

  useFocusEffect(useCallback(() => {
    if (!activityId) {
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);

      // Fetch activity details
      let activityProjectId: number | undefined;
      try {
        const resp = await talentActivitiesApi.get(activityId);
        if (resp) {
          activityProjectId = resp.project;
          setProjectId(resp.project);
          // Fetch specialization from project for student assignment
          if (resp.project) {
            try {
              const proj = await talentProjectsApi.get(resp.project);
              if (proj?.specialization) setSpecializationId(proj.specialization);
            } catch {}
          }
          const startDate = resp.start_at?.substring(0, 10) || resp.start_date || '';
          const endDate = resp.end_at?.substring(0, 10) || resp.end_date || '';
          let dateRange = '';
          if (startDate && endDate) {
            dateRange = formatDateRange(startDate, endDate, isRTL);
          }

          const transformed: ActivityDetailData = {
            id: resp.id,
            title: resp.name || '',
            dateRange,
            description: resp.description || '',
            weight: resp.weight ?? 0,
            location: resp.location || resp.school_name || '',
            studentsCount: resp.students_count ?? resp.students?.length ?? 0,
            teachersCount: resp.teachers_count ?? resp.teachers?.length ?? 0,
            kpisCount: resp.kpis_count ?? resp.kpi_links?.length ?? 0,
            sessionsCount: resp.sessions_count ?? resp.schedules?.length ?? 0,
            overallProgress: resp.overall_progress ?? resp.progress_percentage ?? 0,
            statusDistribution: resp.status_distribution ?? { excellent: 0, good: 0, needsImprovement: 0 },
            kpis: (resp.kpi_links || []).map((k: any) => ({
              id: k.kpi || k.id,
              linkId: k.id,
              name: k.kpi_name || k.name || '',
              targetValue: k.target_value ?? 0,
              achievedValue: k.achieved_value ?? 0,
              unit: k.unit || 'count',
              isAchieved: (k.achieved_value ?? 0) >= (k.target_value ?? 0),
            })),
            sessions: (resp.schedules || []).map((s: any, idx: number) => ({
              id: s.id ?? idx,
              date: s.date || '',
              startTime: s.start_time || '',
              endTime: s.end_time || '',
            })),
            outputs: (resp.outputs || []).map((o: any) => ({
              id: o.id,
              title: o.title || '',
              image: (() => { const src = o.thumbnail || o.file; return src ? (src.startsWith('http') ? src : `${API_BASE_HOST}${src}`) : undefined; })(),
              date: o.created_at || undefined,
            })),
            sustainabilityPlan: resp.sustainability_plan ?? { title: '', content: '' },
          };
          setData(transformed);
        }
      } catch (e) {
        console.log('[ActivityDetail] API failed:', e);
      }

      setIsLoading(false);
    };
    fetchData();
  }, [activityId]));

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
      >
        <View style={[styles.container, { padding: containerPadding }]}>
          <Breadcrumb
            items={[
              { label: t('navigation.home') },
              { label: t('navigation.talents'), route: 'Talents' },
              // Link up to the parent project only once its id is resolved.
              ...(projectId
                ? [{ label: isRTL ? 'تفاصيل المشروع' : 'Project Details', route: 'ProjectDetail', params: { id: projectId, name: entityName || '' } }]
                : []),
              { label: isRTL ? 'تفاصيل النشاط' : 'Activity Details' },
            ]}
          />

          {isLoading ? (
            <TalentsSkeleton />
          ) : (
            <View style={styles.sections}>
              {/* Section 1: Activity Header */}
              <ActivityHeader
                title={data.title}
                dateRange={data.dateRange}
                description={data.description}
                weight={data.weight}
                location={data.location}
                entityName={entityName}
                onEdit={readOnly || isStudent ? undefined : () => navigation.navigate('AddTalentActivity', { activityId })}
                onDelete={readOnly || isStudent ? undefined : async () => {
                  try {
                    await talentActivitiesApi.delete(activityId);
                    navigation.goBack();
                  } catch (e) {
                    console.log('[ActivityDetail] Delete failed:', e);
                  }
                }}
              />

              {/* Section 2: Stats Row (Donut + Progress + 4 Cards) */}
              <DetailStatsRow
                studentsCount={data.studentsCount}
                teachersCount={data.teachersCount}
                activitiesCount={1}
                kpisCount={data.kpisCount}
                programsCount={0}
                sessionsCount={data.sessionsCount}
                overallProgress={data.overallProgress}
                statusDistribution={data.statusDistribution}
              />

              {/* Section 3: KPIs — hidden for students */}
              {!isStudent && (
                <KPIsSection kpis={data.kpis} readOnly={noCreate} onAddKPI={() => navigation.navigate('AddTalentKPI', { activityId })} onViewKPI={(kpiLinkId) => navigation.navigate('TalentKPIDetail' as any, { kpiLinkId })} />
              )}

              {/* Section 4: Sessions */}
              <SessionsSection
                sessions={data.sessions}
                readOnly={noCreate}
                onEdit={() => navigation.navigate('AddTalentActivity', { activityId })}
              />

              {/* Section 5: Calendar — shows this activity's sessions */}
              <ActivityCalendar
                events={calendarEvents}
                hideNewButton
              />

              {/* Section 6: Outputs */}
              <OutputsSection outputs={data.outputs} readOnly={noCreate} projectId={projectId} activityId={activityId} />

              {/* Section 7: Teachers Table — hidden for coordinators and students */}
              {!isCoordinator && !isStudent && <DetailTeachersTable activityId={activityId} readOnly={readOnly} projectId={projectId} specializationId={specializationId} entityName={entityName} />}

              {/* Section 8: Students Table — hidden for students */}
              {!isStudent && <StudentsTable readOnly={readOnly} activityId={activityId} projectId={projectId} specializationId={specializationId} entityName={entityName} />}

              {/* Section 9: Sustainability Plan — hidden for students */}
              {!isStudent && (
                <DevelopmentPlan
                  plan={data.sustainabilityPlan}
                  titleOverride={isRTL ? 'خطة متابعة وتنمية الطلبة' : 'Student Development Plan'}
                  entityId={projectId}
                  entityType="project"
                  readOnly={isCoordinator}
                />
              )}
            </View>
          )}
        </View>
      </ScrollView>
    </PageContainer>
  );
}

/** Format date range like "1 مايو 2026 - 12 سبتمبر 2026" */
function formatDateRange(start: string, end: string, isRTL: boolean): string {
  const AR_MONTHS = [
    'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
    'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
  ];
  const fmt = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    const day = d.getDate();
    const month = isRTL ? AR_MONTHS[d.getMonth()] : d.toLocaleString('en', { month: 'long' });
    const year = d.getFullYear();
    return isRTL ? `${day} ${month} ${year}` : `${month} ${day}, ${year}`;
  };
  return `${fmt(start)} - ${fmt(end)}`;
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  container: {
    flex: 1,
  },
  sections: {
    gap: 20,
  },
});
